import {atom} from "jotai";
import {atomWithStorage, createJSONStorage} from "jotai/utils";

/** sessionStorage key for the user's modality preference within the tab session. */
export const SELECTED_MODALITY_SESSION_KEY = "selected_modality";

/** sessionStorage key for per-course sequence/part selections within the tab session. */
export const SELECTED_OFFERING_SESSION_KEY = "selected_course_offerings";

function createSessionJSONStorage<Value>() {
	return createJSONStorage<Value>(() => {
		if (typeof window === "undefined") {
			return {
				getItem: () => null,
				setItem: () => {},
				removeItem: () => {},
			};
		}
		return sessionStorage;
	});
}

/**
 * Session-persisted modality slug (e.g. `live-online`). Survives soft navigations
 * and hard refresh within the same tab; cleared when the tab closes.
 */
export const selectedModalitySlugAtom = atomWithStorage<string | null>(
	SELECTED_MODALITY_SESSION_KEY,
	null,
	createSessionJSONStorage(),
	{getOnInit: true}
);

/** Selected learning-format modality id for the current course (not persisted). */
export const learningFormatAtom = atom<string | null>(null);

/**
 * True when the current modality selection was applied automatically
 * (URL, session, single format, or campus confirmation). User clicks set this
 * false. Learn More reads it for `modality_auto_select`.
 */
export const modalitySelectionWasAutomaticAtom = atom(false);

/** Selected campus id (from cookie, `?campus=`, or the campus picker). */
export const selectedCampusAtom = atom<string | null>(null);

/**
 * True once campus resolution is ready for modality validation.
 * No longer waits on an automatic geolocation prompt.
 */
export const campusResolutionSettledAtom = atom(false);

/** Selected track id on the course page (when a course has tracks). */
export const selectedTrackAtom = atom<string | null>(null);

/**
 * Per-course sequence/part selection (keyed by course `_id`).
 * Session-persisted so returning to a course restores that course's part, without
 * carrying a part choice across different courses.
 */
export type CourseOfferingSelection = {
	offeringKey: string | null;
	preferredPartSlug: string | null;
};

export type CourseOfferingSelectionsByCourse = Record<
	string,
	CourseOfferingSelection
>;

export const courseOfferingSelectionsAtom =
	atomWithStorage<CourseOfferingSelectionsByCourse>(
		SELECTED_OFFERING_SESSION_KEY,
		{},
		createSessionJSONStorage(),
		{getOnInit: true}
	);
